import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, ChevronRight } from "lucide-react";
import { Seo, PHONE, EMAIL } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";
import { QC_SERVICES_FR } from "../data/quebec";

export default function QuebecCityPage({ city, allCities, pathFn, province = "Québec" }) {
  const nearby = (city.nearby || [])
    .map((s) => allCities.find((c) => c.slug === s))
    .filter(Boolean);

  const faqs = [
    {
      q: `Offrez-vous des agents de sécurité licenciés à ${city.name}?`,
      a: `Oui. 1st Nations Security fournit des agents de sécurité licenciés et professionnels à ${city.name} et dans la région de ${city.region}, en français et en anglais, avec une répartition disponible 24 heures sur 24.`,
    },
    {
      q: `Quels services de sécurité sont disponibles à ${city.name}?`,
      a: `À ${city.name}, nous offrons le gardiennage en uniforme, la patrouille mobile, la sécurité de chantier de construction, la sécurité événementielle, la prévention des pertes, la sécurité concierge, la sécurité industrielle et hospitalière, la surveillance incendie 24/7, la réponse aux alarmes, la protection rapprochée et l'unité cynophile.`,
    },
    {
      q: `Comment embaucher des agents de sécurité à ${city.name}?`,
      a: `Appelez le +1 807-770-0243 ou écrivez à admin@1stnation-security.com. Indiquez-nous votre emplacement à ${city.name}, le type de site et les heures de couverture souhaitées — notre équipe préparera un plan de sécurité pour vous.`,
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid={`qc-city-page-${city.slug}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title={`Services de sécurité ${city.name} | 1st Nations Security`}
        description={`Services de sécurité à ${city.name} : agents licenciés, patrouille mobile, sécurité de chantier et événementielle, surveillance incendie. 24/7 : +1 807-770-0243.`}
        path={pathFn(city)}
        jsonLd={jsonLd}
        lang="fr-CA"
      />
      <nav className="text-xs text-slate-500 mb-6 flex items-center gap-1.5">
        <Link to="/qc" className="hover:text-amber-300">Accueil</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-300">Sécurité à {city.name}</span>
      </nav>

      <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3 flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5" /> {city.name}, {province} — {city.region}
      </p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-6">
        Services de Sécurité à {city.name}
      </h1>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">{city.intro}</p>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-10">
        Que vous ayez besoin d'un agent de sécurité en uniforme pour votre commerce à {city.name}, d'une patrouille
        mobile de nuit, d'une surveillance incendie ou d'une équipe de sécurité événementielle, nos agents licenciés
        sont disponibles 24 heures sur 24, 7 jours sur 7. En tant qu'entreprise détenue à 100 % par des Autochtones,
        nous apportons des valeurs axées sur la communauté à chaque mandat à {city.name}.
      </p>

      <section data-testid="qc-city-services-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          Nos Services à {city.name}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {QC_SERVICES_FR.map((s) => (
            <Link
              key={s.enSlug}
              to={`/qc/services-de-securite/#${s.enSlug}`}
              data-testid={`qc-city-service-link-${s.enSlug}`}
              className="rounded-xl border border-white/10 bg-slate-900/60 p-5 transition-all duration-200 hover:border-amber-500/40 hover:bg-slate-900"
            >
              <h3 className="text-base font-semibold text-white mb-1.5">{s.name} à {city.name}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      <section data-testid="qc-city-industries-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          Secteurs Protégés à {city.name}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
          {city.industries.map((ind) => (
            <div key={ind} className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-900/60 px-5 py-4">
              <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <span className="text-sm text-slate-200">{ind} à {city.name}</span>
            </div>
          ))}
        </div>
      </section>

      {nearby.length > 0 && (
        <section data-testid="qc-city-nearby-section" className="my-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
            Services de Sécurité Près de {city.name}
          </h2>
          <div className="flex flex-wrap gap-3">
            {nearby.map((n) => (
              <Link
                key={n.slug}
                to={pathFn(n)}
                data-testid={`qc-city-nearby-link-${n.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/70 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-amber-500/40 hover:text-amber-300"
              >
                <MapPin className="h-3.5 w-3.5 text-amber-500" /> Sécurité à {n.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section data-testid="qc-city-faq-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          Sécurité à {city.name} — Questions Fréquentes
        </h2>
        <div className="space-y-4 max-w-3xl">
          {faqs.map((f, i) => (
            <details key={i} data-testid={`qc-city-faq-${i}`} className="group rounded-xl border border-white/10 bg-slate-900/60 p-5">
              <summary className="cursor-pointer text-base font-semibold text-white list-none flex items-center justify-between gap-3">
                {f.q}
                <ChevronRight className="h-4 w-4 text-amber-400 shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <CtaBand
        heading={`Embauchez des agents de sécurité à ${city.name}`}
        sub={`Appelez notre répartition 24/7 au ${PHONE} ou écrivez à ${EMAIL} pour des services de sécurité à ${city.name} — gardiennage, patrouille, événements, surveillance incendie et plus.`}
      />
    </div>
  );
}
